import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import type { SignalLogLine, SignalTag } from '@/ui/SignalTerminal'
import { STORAGE_PREFIX } from '@/app/lib/paneKeys'

/**
 * Station Kit v2's `src/app`-side signal-log store (spec 010-improved-design-system,
 * work-order step 6 / task F3). `src/ui/SignalTerminal` is presentational-only
 * (props in, callbacks out) per the `src/ui/**` ESLint boundary rule — this
 * file is the data/wiring half the boundary forbids in `src/ui/`: the
 * `logs`/`open`/`cmd` screen-state (spec §11), the `echo:log` window-event
 * bridge, and the `clear`/`hue`/`warn`/`err` command semantics.
 *
 * Buffer capped at 200 lines (`.slice(-200)`), per spec §10.2.
 */
const MAX_LINES = 200

/**
 * Spec 015 D-12: the "terminal expanded on load" pref `Settings.tsx` already
 * writes (`'1'` open / `'0'` or absent closed) — same `qmc:*` namespace
 * as the other `qmc:` keys.
 */
const TERMINAL_DEFAULT_OPEN_KEY = `${STORAGE_PREFIX}terminal-default-open`

function readTerminalDefaultOpenPref(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(TERMINAL_DEFAULT_OPEN_KEY) === '1'
}

/** 24-hour, no seconds — e.g. `19:30` (spec's copy rule for log-line timestamps). */
function nowLabel(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export interface SignalLogApi {
  logs: SignalLogLine[]
  open: boolean
  toggleOpen: () => void
  /** Controlled command-line value, mirrored into `SignalTerminal`'s `cmd`/`onCmdChange` props. */
  cmd: string
  setCmd: (value: string) => void
  /** Parses and runs one of `clear` / `hue <0-360>` / `warn <text>` / `err <text>`; anything else logs `Unknown command: x`. */
  submitCommand: (raw: string) => void
  /**
   * **FROZEN emit contract (spec 010 task F3).** This is the one call every
   * Phase-P screen uses to report a mutation: `emit('save' | 'warn' | 'err', message)`.
   * Do not change this signature once Phase P opens — six screens call it
   * directly via `useSignalLog().emit`. Severity colouring
   * (save=`--paper`, warn=AMBER `#CB8A00`, err=`--alert`) lives in
   * `SignalTerminal`, driven purely by `tag`.
   */
  emit: (tag: SignalTag, msg: string) => void
}

const SignalLogContext = createContext<SignalLogApi | undefined>(undefined)

/** `window.dispatchEvent(new CustomEvent('echo:log', { detail: { tag, msg } }))` — the boundary-respecting seam a future `I1` (real API/WS failure paths) uses without importing app state into `src/ui/`. */
export interface EchoLogEventDetail {
  tag: SignalTag
  msg: string
}

/** Spec 004 P3: QMC's own name and version in the boot line (echo prints `station v0.23.0`). Bump with the backend version. */
export const STATION_NAME = 'Quant Mission Control'
export const STATION_VERSION = '0.4.0'

/**
 * Mounted once, above `StationShell`'s terminal mount (spec 010 §4.1). Seeds
 * the shell-boot line `Session restored — operator EKIN, Quant Mission Control v0.4.0.`
 * (`operator` defaults to `EKIN` only when the caller has no real username to
 * hand it — the real app always passes the authenticated operator's own
 * username).
 */
export function SignalLogProvider({ children, operator = 'EKIN' }: { children: ReactNode; operator?: string }) {
  const [logs, setLogs] = useState<SignalLogLine[]>(() => [
    { at: nowLabel(), tag: 'save', msg: `Session restored — operator ${operator}, ${STATION_NAME} v${STATION_VERSION}.` },
  ])
  const [open, setOpen] = useState(readTerminalDefaultOpenPref)
  const [cmd, setCmd] = useState('')

  const emit = useCallback((tag: SignalTag, msg: string) => {
    setLogs((previous) => [...previous, { at: nowLabel(), tag, msg }].slice(-MAX_LINES))
  }, [])

  const submitCommand = useCallback(
    (raw: string) => {
      const trimmed = raw.trim()
      setCmd('')
      if (!trimmed) return

      const [word, ...rest] = trimmed.split(/\s+/)
      const arg = rest.join(' ')

      switch (word) {
        case 'clear':
          setLogs([])
          return
        case 'hue': {
          const degrees = Number(arg)
          if (arg !== '' && Number.isFinite(degrees)) {
            document.documentElement.style.setProperty('--h', String(degrees))
            return
          }
          emit('err', `Unknown command: ${trimmed}`)
          return
        }
        case 'warn':
          emit('warn', arg || 'warn')
          return
        case 'err':
          emit('err', arg || 'err')
          return
        default:
          emit('err', `Unknown command: ${trimmed}`)
      }
    },
    [emit],
  )

  // The `echo:log` window-event bridge (spec §3's boundary note): any
  // API/WS failure path anywhere in the app (wired for real in a future `I1`)
  // dispatches `new CustomEvent('echo:log', { detail: { tag, msg } })` and it
  // lands here without either side importing the other.
  useEffect(() => {
    function handleEchoLog(event: Event) {
      const detail = (event as CustomEvent<EchoLogEventDetail>).detail
      if (!detail) return
      emit(detail.tag, detail.msg)
    }

    window.addEventListener('echo:log', handleEchoLog)
    return () => window.removeEventListener('echo:log', handleEchoLog)
  }, [emit])

  const value = useMemo<SignalLogApi>(
    () => ({
      logs,
      open,
      toggleOpen: () => setOpen((previous) => !previous),
      cmd,
      setCmd,
      submitCommand,
      emit,
    }),
    [logs, open, cmd, submitCommand, emit],
  )

  return <SignalLogContext.Provider value={value}>{children}</SignalLogContext.Provider>
}

export function useSignalLog(): SignalLogApi {
  const context = useContext(SignalLogContext)
  if (!context) throw new Error('useSignalLog must be used within a SignalLogProvider')
  return context
}

/**
 * Convenience guard for "log this record's open exactly once per selection"
 * (spec §4.1's rail-select-logs-the-open-line rule) — a plain `useEffect`
 * keyed on the record id would double-fire under StrictMode's dev-mode
 * double-invoke, since `emit` itself is stable but nothing else stops a
 * second call for the same id within the same mount. Callers pass the
 * current id (or `undefined` when nothing is selected) and a factory for the
 * message; the ref persists across the double-invoke so only one line is
 * ever appended per distinct id.
 */
export function useLogOncePerId(id: string | number | undefined, build: () => string, tag: SignalTag = 'save') {
  const { emit } = useSignalLog()
  const lastLoggedRef = useRef<string | number | undefined>(undefined)
  // `build`/`tag` are read through refs rather than listed as effect
  // dependencies — this must fire once per `id` transition only, never again
  // just because the caller re-rendered with a fresh closure (which `build`
  // always is, being an inline arrow function at the call site). Refs are
  // synced in their own effect (never written during render, per
  // `react-hooks/refs`) that always runs before the guarded effect below,
  // since effects commit in declaration order.
  const buildRef = useRef(build)
  const tagRef = useRef(tag)

  useEffect(() => {
    buildRef.current = build
    tagRef.current = tag
  })

  useEffect(() => {
    if (id === undefined) return
    if (lastLoggedRef.current === id) return
    lastLoggedRef.current = id
    emit(tagRef.current, buildRef.current())
  }, [id, emit])
}
